import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-md space-y-6" data-testid="forgot-password-card">
        <div className="text-center">
          <div className="font-display text-2xl tracking-widest font-semibold">THE&nbsp;WOODLAND</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">ERP · Password reset</div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
          {!sent ? (
            <>
              <h1 className="text-lg font-semibold mb-1">Forgot your password?</h1>
              <p className="text-sm text-muted-foreground mb-4">Enter the email you use to sign in. If we find a matching account, we&rsquo;ll email you a reset link valid for 30 minutes.</p>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="forgot-email" />
                </div>
                <Button type="submit" disabled={submitting || !email} className="w-full bg-primary text-primary-foreground" data-testid="forgot-submit">
                  {submitting ? "Sending…" : (<><Mail className="w-4 h-4 mr-1" /> Send reset link</>)}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
              <h2 className="text-base font-semibold mb-1">Check your inbox</h2>
              <p className="text-sm text-muted-foreground">If an account exists for <span className="font-medium text-foreground">{email}</span>, a reset link is on its way. It expires in 30 minutes.</p>
              <p className="text-[11px] text-muted-foreground mt-3">Didn&rsquo;t get it? Check spam, or try again in a minute.</p>
            </div>
          )}
        </div>
        <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1" data-testid="forgot-back">
          <ArrowLeft className="w-3 h-3" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
